import React from 'react';
import { Button, Card, Skeleton } from 'antd';
import {
  CheckCircleOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { ProfileStatus, ProfileData } from '../../types/participant.types';
import { calculateAgeRange, getAgeRangeLabel } from '../../utils/age-range';
import { genderLabels, educationLabels, economicLabels } from '../../utils/profile-labels';


interface ProfileStatusCardProps {
  profile: ProfileData | null;
  isLoading?: boolean;
  error?: string | null;
  onEditClick?: () => void;
}

const STATUS_THEME = {
  current: {
    background: 'linear-gradient(135deg, #237804 0%, #135200 100%)',
    icon: <CheckCircleOutlined style={{ fontSize: 22 }} />,
    title: 'Perfil atualizado',
    buttonColor: '#237804',
    buttonText: 'Editar Perfil',
  },
  stale: {
    background: 'linear-gradient(135deg, #d46b08 0%, #ad4e00 100%)',
    icon: <ExclamationCircleOutlined style={{ fontSize: 22 }} />,
    title: 'Perfil desatualizado',
    buttonColor: '#d46b08',
    buttonText: 'Revisar Perfil',
  },
  empty: {
    background: 'linear-gradient(135deg, #cf1322 0%, #820014 100%)',
    icon: <UserOutlined style={{ fontSize: 22 }} />,
    title: 'Perfil não preenchido',
    buttonColor: '#cf1322',
    buttonText: 'Completar Perfil',
  },
};

function buildProfileSummary(profile: ProfileData): string {
  const parts: string[] = [];
  if (profile.gender) parts.push(genderLabels[profile.gender] ?? profile.gender);
  if (profile.dateOfBirth) parts.push(getAgeRangeLabel(calculateAgeRange(profile.dateOfBirth)));
  if (profile.educationLevel) parts.push(educationLabels[profile.educationLevel] ?? profile.educationLevel);
  if (profile.economicLevel) parts.push(economicLabels[profile.economicLevel] ?? profile.economicLevel);
  if (profile.city) parts.push(`${profile.city.name}, ${profile.city.state.name}`);
  if (profile.lastUpdatedAt) parts.push(`atualizado ${dayjs(profile.lastUpdatedAt).format('DD/MM/YYYY')}`);
  return parts.join(' · ');
}

function resolveTheme(profile: ProfileData) {
  if (profile.status === ProfileStatus.NEVER_FILLED || profile.isIncomplete) {
    return {
      theme: STATUS_THEME.empty,
      subtitle: 'Complete seu perfil para participar de pesquisas',
    };
  }
  if (profile.status === ProfileStatus.STALE) {
    return {
      theme: STATUS_THEME.stale,
      subtitle: buildProfileSummary(profile) || 'Seu perfil está desatualizado há mais de 90 dias',
    };
  }
  return {
    theme: STATUS_THEME.current,
    subtitle: buildProfileSummary(profile),
  };
}

const ProfileStatusCardComponent: React.FC<ProfileStatusCardProps> = ({
  profile,
  isLoading = false,
  error = null,
  onEditClick,
}) => {
  if (isLoading) {
    return <Skeleton.Button active block style={{ height: 88, borderRadius: 8 }} />;
  }

  if (error || !profile) {
    return null;
  }

  const { theme, subtitle } = resolveTheme(profile);

  return (
    <Card
      style={{ background: theme.background, border: 'none', borderRadius: '8px' }}
      styles={{ body: { padding: '24px' } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: 'white',
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {theme.icon}
            {theme.title}
          </div>
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.95)' }}>{subtitle}</p>
        </div>

        <Button
          size="large"
          icon={<EditOutlined />}
          onClick={onEditClick}
          style={{
            backgroundColor: 'white',
            color: theme.buttonColor,
            border: 'none',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {theme.buttonText}
        </Button>
      </div>
    </Card>
  );
};

export const ProfileStatusCard = React.memo(ProfileStatusCardComponent);

export default ProfileStatusCard;
